import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import type { SaveUserConfigPayload } from "@hooks/use-report-layout";

interface UseUpdateReportUserConfigResponse {
  id?: number;
  report_id: string;
  user_id: number;
  override_config: Record<string, any>;
  title?: string;
  description?: string;
  updated_at?: string;
}

/**
 * Hook to save/update user report configuration (show/hide/reorder charts)
 * Calls: POST /wp-json/smassets/v1/reports/{reportId}/user-config
 */
export const useUpdateReportUserConfig = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const updateUserConfig = useCallback(
    async (
      reportId: string,
      payload: SaveUserConfigPayload
    ): Promise<UseUpdateReportUserConfigResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl(`wp-json/smassets/v1/reports/${reportId}/user-config`);

        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "POST",
            body: JSON.stringify(payload),
            headers: {
              "Authorization": `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(`Failed to save report configuration: ${response.status}`);
        }

        const result = await response.json();
        setLoading(false);
        return result as UseUpdateReportUserConfigResponse;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to save report configuration";
        setError(errorMessage);
        setLoading(false);
        return null;
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  return {
    updateUserConfig,
    loading,
    error,
  };
};
